/* eslint-disable max-len */
/* eslint-disable radix */
/* eslint-disable react-hooks/exhaustive-deps */

import { Button, CircularProgress } from '@material-ui/core';
import React, { useEffect, useState } from 'react';
import { PAYMENT_TYPE, ROUTES } from 'utils/constants';
import { useModal, useStores } from 'hooks';
import { useSnackbar } from 'notistack';
import { PayboxService, SubscriptionService } from 'services';
import { FormHelper, translate } from 'utils';
import { Alert } from '@material-ui/lab';
import { useHistory } from 'react-router-dom';

/* ---------------------------------------------------------
   🔧 UTILITAIRES
--------------------------------------------------------- */

const checkPrice = (paymentInformation, setStatus) => {
  if (!paymentInformation?.price && paymentInformation?.price !== 0) {
    setStatus('errorPaybox');
    return false;
  }
  return true;
};

const createShoppingCartXML = () => {
  const xmlDoc = document.implementation.createDocument('', '', null);
  const shoppingCartElement = xmlDoc.createElement('shoppingcart');
  const totalElement = xmlDoc.createElement('total');
  const totalQuantityElement = xmlDoc.createElement('totalQuantity');

  totalQuantityElement.textContent = 1;
  totalElement.appendChild(totalQuantityElement);
  shoppingCartElement.appendChild(totalElement);

  return `<?xml version='1.0' encoding='utf-8'?>${new XMLSerializer().serializeToString(shoppingCartElement)}`;
};

const createBillingXML = (userInfos) => {
  const xmlDoc = document.implementation.createDocument('', '', null);
  const billingElement = xmlDoc.createElement('Billing');
  const addressElement = xmlDoc.createElement('Address');

  const add = (tag, value) => {
    const el = xmlDoc.createElement(tag);
    el.textContent = value || '';
    addressElement.appendChild(el);
  };

  add('FirstName', userInfos.firstName);
  add('LastName', userInfos.lastName);
  add('Address1', userInfos.address?.address1 || userInfos.address1);
  add('ZipCode', userInfos.address?.postalCode || userInfos.postalCode);
  add('City', userInfos.address?.city || userInfos.city);

  const countryId = userInfos?.address
    ? FormHelper.getDefaultCountry(userInfos.address).id
    : userInfos?.country?.id || '250';

  add('CountryCode', countryId);

  billingElement.appendChild(addressElement);

  return `<?xml version='1.0' encoding='utf-8'?>${new XMLSerializer().serializeToString(billingElement)}`;
};

const buildTemporaryModel = (paymentInformation, typeId) => {
  return {
    tranId: paymentInformation.tranId || `TMP_${typeId}_${Date.now()}`,
    nom: paymentInformation.nom || paymentInformation.lastName || '',
    prenom: paymentInformation.prenom || paymentInformation.firstName || '',
    idCivilite: paymentInformation.idCivilite ?? 0,
    dateNaissance: paymentInformation.dateNaissance ?? null,

    contact: {
      courrielPersonnel: paymentInformation.contact?.courrielPersonnel ?? paymentInformation.email ?? '',
      telephonePortable1: paymentInformation.contact?.telephonePortable1 ?? ''
    },

    adresse: {
      ligne1: paymentInformation.adresse?.ligne1 ?? '',
      ligne2: paymentInformation.adresse?.ligne2 ?? '',
      ligne3: paymentInformation.adresse?.ligne3 ?? '',
      codePostal: paymentInformation.adresse?.codePostal ?? '',
      ville: paymentInformation.adresse?.ville ?? '',
      pays:
        typeof paymentInformation.adresse?.pays === 'object'
          ? paymentInformation.adresse.pays.label
          : paymentInformation.adresse?.pays ?? '',
      npai: paymentInformation.adresse?.npai ?? false
    },

    abonnementsModel: paymentInformation.abonnementsModel || [],
    payload: JSON.stringify(paymentInformation)
  };
};

/* ---------------------------------------------------------
   🎯 COMPOSANT PRINCIPAL
--------------------------------------------------------- */

export const PayboxPayment = ({
  getPaymentInformations,
  disabled = false,
  typeId,
  buttonLabel = 'button.validate',
  buttonId,
  providedEmail = null
}) => {
  const { userStore } = useStores();
  const { userConnected } = userStore;
  const showModal = useModal();
  const history = useHistory();
  const { enqueueSnackbar } = useSnackbar();

  const [status, setStatus] = useState('ready');
  const [email, setEmail] = useState('');

  useEffect(() => {
    setEmail(providedEmail || userConnected.email);
  }, []);

  const handleSubmit = async () => {
    setStatus('sending');

    const paymentInformation = await getPaymentInformations();
    if (!paymentInformation || !checkPrice(paymentInformation, setStatus)) {
      return;
    }

    const userInfos =
      userConnected || JSON.parse(localStorage.getItem('subscriber') || '{}');

    /* ---------------------------------------------------------
       🔒 SUBSCRIPTION ONLY — logique spécifique
    --------------------------------------------------------- */

    let tempId = null;
    let pbxType = typeId;

    if (typeId === PAYMENT_TYPE.SUBSCRIPTION.code) {
      // 1) Check subscriber exist
      const exists = await SubscriptionService.isSubscriberExists({
        nom: paymentInformation.nom,
        prenom: paymentInformation.prenom,
        codePostal: paymentInformation.adresse?.codePostal,
        courrielPersonnel: paymentInformation.contact?.courrielPersonnel
      });

      if (exists?.data) {
        showModal({
          type: 'SUBSCRIBER_EXISTS',
          onProceed: () => history.push(ROUTES.CONNECT),
          subscriber: paymentInformation,
          onCancel: () => setStatus('ready')
        });
        return;
      }

      // 2) Create temporary subscription
      const tempModel = buildTemporaryModel(paymentInformation, typeId);
      const response = await SubscriptionService.saveTemporarySubscription(tempModel);
      tempId = response.data;

      if (!tempId) {
        setStatus('errorPaybox');
        return;
      }

      // Paybox abonnement v2
      pbxType = 20;
    }

    /* ---------------------------------------------------------
       💳 PAYBOX COMMON FLOW (⚠️ CRITICAL PART)
    --------------------------------------------------------- */

    const price = parseFloat(paymentInformation.price) * 100;
    const time = new Date().toISOString().split('.')[0];

    const pbxCommand =
      typeId === PAYMENT_TYPE.SUBSCRIPTION.code
        ? `CMD_${typeId}_${tempId}`
        : `Command${typeId}_${paymentInformation.itemId}_${time}`;

    // ✅ RESTORE HISTORICAL BEHAVIOR
    const pbxEffectue =
      typeId === PAYMENT_TYPE.SUBSCRIPTION.code
        ? `https://${window.location.host}${ROUTES.CONFIRM_SUBSCRIPTION}`
        : `https://${window.location.host}${ROUTES.PAYBOX_CONFIRM(
            `typeId=${typeId}${paymentInformation.confirmParameter || ''}`
          )}`;

    // ✅ BACK CALLBACK ONLY FOR SUBSCRIPTION V2
    const pbxRetour =
      typeId === PAYMENT_TYPE.SUBSCRIPTION.code
        ? `${process.env.REACT_APP_API_URL || ''}/Paybox/abonnement-v2?tempId=${tempId}`
        : undefined;

    const shoppingCartXML = createShoppingCartXML();
    const billingXML = createBillingXML(userInfos);

    PayboxService.getHmac({
      PBX_TOTAL: price,
      PBX_CMD: pbxCommand,
      PBX_PORTEUR: email,
      PBX_TIME: time,
      PBX_EFFECTUE: pbxEffectue,
      PBX_TYPE: pbxType,
      PBX_SHOPPINGCART: shoppingCartXML,
      PBX_BILLING: billingXML,
      ...(pbxRetour ? { PBX_RETOUR: pbxRetour } : {})
    })
      .then(hmac => {
        PayboxService.gotoPaymentPage(hmac);
      })
      .catch(() => {
        enqueueSnackbar(translate('errors.errorPaybox'), { variant: 'error' });
        setStatus('errorPaybox');
      });
  };

  const renderSwitchStatus = () => {
    if (status === 'sending') return <CircularProgress />;
    if (status === 'ready')
      return (
        <Button color="primary" disabled={disabled} id={buttonId} onClick={handleSubmit}>
          {translate(buttonLabel)}
        </Button>
      );
    return <Alert severity="error">{translate(`errors.${status}`)}</Alert>;
  };

  return <>{renderSwitchStatus()}</>;
};

export default PayboxPayment;